/**
 * InfluencerCard — the ONE card behind both T212648 ("Influencers you might like" on the
 * dashboard) and T212649 (the discovery section on the profile). The two tickets share one
 * design, so they share this component; building one per ticket would have produced two
 * cards that drift apart.
 *
 * Shape (design spec §2.3):
 *   banner (photo OR colour bands) -> ringed centred avatar overlapping the banner edge ->
 *   centred username + inline badge -> "N followers - N collections" -> overlapping swatch
 *   stack with "+N more" -> "N colour(s) in common" -> outlined Follow pill.
 *
 * Reuse, not rebuild:
 *   - the badge is shared/username-with-badge.jsx, in its `user-badge--centred` variant,
 *     which T212588 added specifically for this card;
 *   - the follow pill is follow-member/follow-member-button.jsx;
 *   - the follow/unfollow calls are js/api/follow-member/follow-member.js.
 *
 * Follow state is owned by the PARENT, not by this card. T212649 needs an unfollow to
 * remove the row and stay undoable for ~5 seconds, which only the list can coordinate — so
 * the card reports intent through `onFollowChange` and renders whatever it is told.
 */
import { useState } from "react";
import { UsernameWithBadge } from "../shared/username-with-badge";
import { FollowMemberButton } from "../follow-member/follow-member-button";
import {
  useCmsFollowMember,
  useCmsUnfollowMember,
} from "../../../js/api/follow-member/follow-member.js";
import { SwatchStack } from "./swatch-stack";

/**
 * Banner fallback. With no cover image the design uses a full-bleed colour-band fill built
 * from the shared colours — a genuine variant, not a broken-image state, so a member who
 * has never uploaded a cover still gets a complete card.
 */
const BannerFill = ({ swatches }) => {
  const bands = swatches.slice(0, 4);

  if (!bands.length) {
    return <div className="influencer-card__banner-fill influencer-card__banner-fill--default" />;
  }

  return (
    <div className="influencer-card__banner-fill">
      {bands.map((swatch, index) => (
        <span
          key={`${swatch.colourCode}-${index}`}
          className="influencer-card__banner-band"
          style={{ backgroundColor: `rgb(${swatch.rgb})` }}
        />
      ))}
    </div>
  );
};

export const InfluencerCard = ({ influencer, copy, onFollowChange }) => {
  const {
    memberId,
    username,
    displayName,
    profileUrl,
    avatarUrl,
    bannerImageUrl,
    badge,
    metaLabel,
    sharedColours = [],
    sharedColourOverflow = 0,
    sharedColoursLabel,
    isFollowed,
    isEditorialPick,
  } = influencer;

  const [isPending, setIsPending] = useState(false);

  const followMutation = useCmsFollowMember();
  const unfollowMutation = useCmsUnfollowMember();

  const handle = username || displayName;

  const toggleFollow = () => {
    if (isPending) {
      return;
    }

    setIsPending(true);
    const nextFollowed = !isFollowed;
    const mutation = nextFollowed ? followMutation : unfollowMutation;

    mutation.mutateAsync({
      id: memberId,
      successHandler: () => {
        setIsPending(false);
        onFollowChange?.(influencer, nextFollowed);
      },
      errorHandler: () => {
        setIsPending(false);
        // Left as-is on failure: the button keeps its previous state rather than lying
        // about a follow that did not happen.
      },
    });
  };

  return (
    <article className="influencer-card" data-testid="influencer-card" data-member-id={memberId}>
      <div className="influencer-card__banner">
        {bannerImageUrl ? (
          <img
            className="influencer-card__banner-image"
            src={bannerImageUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
        ) : (
          <BannerFill swatches={sharedColours} />
        )}
      </div>

      <a className="influencer-card__avatar-link" href={profileUrl} tabIndex={-1} aria-hidden="true">
        {avatarUrl ? (
          <img className="influencer-card__avatar" src={avatarUrl} alt="" loading="lazy" />
        ) : (
          <span className="influencer-card__avatar influencer-card__avatar--placeholder">
            {(handle || "?").charAt(0).toUpperCase()}
          </span>
        )}
      </a>

      <div className="influencer-card__body">
        <UsernameWithBadge
          username={handle}
          profileUrl={profileUrl}
          badge={badge}
          prefixWithAt={!!username}
          className="user-badge--centred"
          truncate
        />

        <p className="influencer-card__meta" data-testid="influencer-card-meta">
          {metaLabel}
        </p>

        <SwatchStack
          swatches={sharedColours}
          overflow={sharedColourOverflow}
          moreLabelTemplate={copy.moreSwatches}
        />

        {/*
          Editorial picks are padding — they have no affinity to state, so the line is
          omitted rather than rendered as "0 colours in common".
        */}
        {!isEditorialPick && !!sharedColoursLabel && (
          <p className="influencer-card__affinity" data-testid="influencer-card-affinity">
            {sharedColoursLabel}
          </p>
        )}

        <FollowMemberButton
          isFollowing={isFollowed}
          isLoading={isPending}
          toggleClickHandler={toggleFollow}
          followTitle={copy.followLabel}
          followingTitle={copy.followingLabel}
          isSmall
          data-testid="influencer-card-follow"
          aria-label={`${isFollowed ? copy.followingLabel : copy.followLabel} ${handle}`}
        />
      </div>
    </article>
  );
};
